<script>
import { useToast } from '@/composables/useToast';

// The older `v-model` alert some pages still use. It now hands the message
// to the app's toaster (useToast), so every alert appears in the same
// top-right stack and closes itself, instead of a second toast in the
// middle of the screen. `modelValue` is reset straight away, so setting
// `show = true` again shows the next message.

// Generic headers callers pass by habit; they add no information, so the
// toast shows only the message for these.
const GENERIC_HEADERS = ['system message', 'server error'];

const DURATIONS = { success: 4000, info: 4000, warning: 6000, error: 6000 };
const TYPE_ALIASES = { primary: 'success', danger: 'error' };

export default {
  name: 'FadeAwayMessage',
  props: {
    modelValue: { type: Boolean, default: false },
    header: { type: String, default: '' },
    message: { type: String, default: '' },
    // Auto-dismiss delay in ms. Omit to use the default for the type.
    timeout: { type: [Number, String], default: null },
    type: { type: String, default: 'success' },
    // Accepted for compatibility with existing callers; every toast is in
    // the top-right corner now.
    displayType: { type: String, default: 'default' },
    top: { type: [Number, String], default: null },
    width: { type: [Number, String], default: null },
  },
  emits: ['update:modelValue'],

  setup() {
    return { toast: useToast() };
  },

  computed: {
    kind() {
      const key = TYPE_ALIASES[this.type] ?? this.type;
      return key in DURATIONS ? key : 'info';
    },
    isGenericHeader() {
      const header = (this.header ?? '').trim().replace(/!$/, '').toLowerCase();
      return !header || GENERIC_HEADERS.some((g) => header.startsWith(g));
    },
    // A few callers put the actual text in `header` and leave `message`
    // empty; show it as the message in that case.
    body() {
      if (this.message) return this.message;
      if (!this.isGenericHeader) return this.header;
      return this.kind === 'error' ? 'Something went wrong. Please try again.' : 'Done.';
    },
    title() {
      return this.message && !this.isGenericHeader ? this.header : '';
    },
    duration() {
      const custom = Number(this.timeout);
      return custom > 0 ? custom : DURATIONS[this.kind];
    },
  },

  // Renders nothing itself; UiToaster shows the message.
  render: () => null,

  watch: {
    modelValue: {
      handler(visible) {
        if (!visible) return;
        this.toast.show(this.kind, this.body, this.title, this.duration);
        this.$emit('update:modelValue', false);
      },
      immediate: true,
    },
  },
};
</script>
